import { WIDGET_METRIC_GROUPS } from "@services/math/math.const";
import { MetricResponse, SalesPerformanceApi } from "./sales-performance.api";
import { format } from "date-fns";
import { WidgetMetricKey } from "@services/math/math.service";

interface ChartDataPoint {
  date: string;
  [key: string]: number | string;
}

export class SalesPerformanceService {
  private readonly api: SalesPerformanceApi;

  constructor() {
    this.api = new SalesPerformanceApi();
  }

  public async fetchMetricData(
    metric: string,
    startDate: string,
    endDate: string,
  ): Promise<
    {
      date: string;
      value: number;
    }[]
  > {
    try {
      const cuttedMetric = metric
        .replace("cfo", "")
        .replace("total", "")
        .replace("organic", "")
        .replace("ad", "")
        .replace("coo", "")
        .replace("market", "");

      const formattedMetric =
        cuttedMetric.slice(0, 1).toUpperCase() + cuttedMetric.slice(1);

      const metricGroup = WIDGET_METRIC_GROUPS.find((group) =>
        group.keys.includes(metric as WidgetMetricKey),
      ).title;

      let metricPrefix = "";

      if (metricGroup === "CFO") {
        metricPrefix = "Cfo";
      }

      if (metricGroup === "Total") {
        metricPrefix = "Total";
      }

      if (metricGroup === "Organic") {
        metricPrefix = "Organic";
      }

      if (metricGroup === "Ad") {
        metricPrefix = "Ad";
      }

      const apiMethod = this.api[`get${metricPrefix}${formattedMetric}`];

      if (typeof apiMethod !== "function") {
        console.warn(
          `API method for metric ${formattedMetric + metric} not found. Please check the metric name.`,
        );

        return [];
      }

      const metricData: MetricResponse | number = await apiMethod(
        startDate,
        endDate,
      );

      if (typeof metricData === "number") {
        console.warn(
          `Received a number instead of MetricResponse for metric ${metric}.`,
        );
        const dates = this.generateDateRange(
          new Date(startDate),
          new Date(endDate),
        );
        return dates.map((date) => ({
          date,
          value: metricData,
        }));
      }

      return metricData.data.map((item) => ({
        date: format(item.period_end, "MMM dd"),
        value: item.value,
      }));
    } catch (error) {
      console.error(`Error fetching ${metric}:`, error);
      return [];
    }
  }

  public async getChartData(
    metrics: string[],
    startDate: Date,
    endDate: Date,
  ): Promise<ChartDataPoint[]> {
    const formattedStartDate = format(startDate, "yyyy-MM-dd");
    const formattedEndDate = format(endDate, "yyyy-MM-dd");

    console.log(metrics);

    const metricsData = await Promise.all(
      metrics.map((metric) =>
        this.fetchMetricData(metric, formattedStartDate, formattedEndDate),
      ),
    );

    const chartData: ChartDataPoint[] = [];

    metricsData.forEach((data, index) => {
      const metricName = metrics[index];
      data.forEach((item) => {
        const existingPoint = chartData.find(
          (point) => point.date === item.date,
        );
        if (existingPoint) {
          existingPoint[metricName] = item.value;
        } else {
          chartData.push({ date: item.date, [metricName]: item.value });
        }
      });
    });

    return chartData;
  }

  private generateDateRange(start: Date, end: Date): string[] {
    const dates: string[] = [];
    const current = new Date(start);

    while (current <= end) {
      dates.push(format(current, "MMM dd"));
      current.setDate(current.getDate() + 1);
    }

    return dates;
  }
}
